import type { PanelBlock } from '../../types'
import { anchorGroup, type AnchorType, RAIL_LENGTHS_MM, type RailSize } from './FastRackCatalogue'

/**
 * Module dimension (mm) along the horizontal rail direction.
 * PanelBlock.panel holds raw catalog dimensions (not orientation-adjusted),
 * so landscape blocks need the catalog height (the physically longer edge
 * that runs horizontally when the panel is rotated).
 */
export function getModuleLengthAlongRail(block: PanelBlock): number {
  return block.orientation === 'landscape' ? block.panel.height : block.panel.width
}

/**
 * Normalise the preferredRails option from checkbox string[] or legacy
 * dropdown string into a RailSize[].  Falls back to all three sizes when
 * the resolved set is empty (e.g. user unchecked every box).
 *
 * @returns `{ sizes, fellBack }` — `fellBack` is true when an empty
 *   selection was replaced by the full set.
 */
export function normalizePreferredRails(value: unknown): { sizes: RailSize[]; fellBack: boolean } {
  const VALID: Record<string, RailSize> = { '94': 94, '139': 139, '185': 185 }
  const ALL_SIZES: RailSize[] = [94, 139, 185]

  if (Array.isArray(value)) {
    const sizes: RailSize[] = []
    for (const v of value) {
      const mapped = VALID[String(v)]
      if (mapped) sizes.push(mapped)
    }
    if (sizes.length > 0) return { sizes, fellBack: false }
    return { sizes: ALL_SIZES, fellBack: true }
  }

  return { sizes: parsePreferredRails(typeof value === 'string' ? value : 'all'), fellBack: false }
}

export const INTER_MODULE_GAP_MM = 12.75
export const MM_PER_INCH = 25.4
/** ½" panel edge gap for perimeter trim / critter guard (FR-ULT install manual). */
export const PANEL_EDGE_GAP_MM = 13
export const END_BUFFER_MM = 101.75
export const FIFTY_FT_MM = 15_240
/** Thermal splice joint module gap (standard end clamps). */
export const THERMAL_JOINT_MODULE_GAP_IN = 4.75
/** Thermal splice joint rail gap (hidden end clamps). */
export const THERMAL_JOINT_RAIL_GAP_IN = 0.75
export const THERMAL_JOINT_MODULE_GAP_MM = THERMAL_JOINT_MODULE_GAP_IN * MM_PER_INCH
export const THERMAL_JOINT_RAIL_GAP_MM = THERMAL_JOINT_RAIL_GAP_IN * MM_PER_INCH

/** Panel block dimensions are stored in mm; pest-protection formulas use inches. */
export function panelMmToInches(mm: number): number {
  return mm / MM_PER_INCH
}

/** Horizontal inter-module gap along a row (mm), from block spacing or default catalog gap. */
export function getInterModuleGapMm(block: PanelBlock): number {
  const gapM = block.panelSpacing?.[0]
  if (gapM != null && gapM > 0) {
    return gapM * 1000
  }
  return INTER_MODULE_GAP_MM
}

export function computeRowLength(
  numModules: number,
  moduleLength_mm: number,
  useHiddenEndClamps = false,
  interModuleGap_mm = INTER_MODULE_GAP_MM
): number {
  if (numModules <= 0) return 0

  let rowLen = numModules * moduleLength_mm + (numModules - 1) * interModuleGap_mm
  rowLen += END_BUFFER_MM

  if (rowLen > FIFTY_FT_MM) {
    rowLen += useHiddenEndClamps ? THERMAL_JOINT_RAIL_GAP_MM : THERMAL_JOINT_MODULE_GAP_MM
  }

  return rowLen
}

export function computeSpliceBreaks(rowLen_mm: number): number {
  if (rowLen_mm < FIFTY_FT_MM) return 0
  return Math.floor(rowLen_mm / FIFTY_FT_MM)
}

export interface ClampCounts {
  midClamps: number
  endClamps: number
  stoppers: number
  bondingJumpers: number
}

/**
 * Compute clamp, stopper, and bonding jumper quantities for a single contiguous row.
 *
 * When `clickLockNonHidden` is true, end positions use a mid clamp + stopper instead
 * of a dedicated end clamp SKU, so `midClamps` is increased by `endClamps` and
 * `stoppers` equals the end position count.
 */
export function computeClampCounts(numPanels: number, spliceBreaks: number, clickLockNonHidden: boolean): ClampCounts {
  if (numPanels === 0) return { midClamps: 0, endClamps: 0, stoppers: 0, bondingJumpers: 0 }

  let midClamps = numPanels < 2 ? 0 : 2 * (numPanels - 1)
  let endClamps = 4 // 2 ends × 2 rails
  let stoppers = 0
  const bondingJumpers = spliceBreaks * 2

  endClamps += spliceBreaks * 4
  midClamps = Math.max(0, midClamps - spliceBreaks * 2)

  if (clickLockNonHidden) {
    midClamps += endClamps
    stoppers = endClamps
  }

  return { midClamps, endClamps, stoppers, bondingJumpers }
}

// --- Best-Fit Rail Selection ---

export interface RailCombination {
  counts: Partial<Record<RailSize, number>>
  totalLength_mm: number
  pieces: number
}

/**
 * Recursive greedy search for the shortest combination of stock rails that
 * covers `rowLen_mm`. Tries sizes largest-first to avoid duplicate permutations.
 * Returns null only when `preferredRails` is empty or `rowLen_mm <= 0`.
 */
export function getRails(rowLen_mm: number, preferredRails: RailSize[]): RailCombination | null {
  if (preferredRails.length === 0 || rowLen_mm <= 0) return null

  const sorted = [...preferredRails].sort((a, b) => b - a)
  let best: RailCombination | null = null

  function recurse(currentLen: number, counts: Partial<Record<RailSize, number>>, stageIndex: number): void {
    if (currentLen >= rowLen_mm) {
      const pieces = Object.values(counts).reduce((sum, c) => sum + (c ?? 0), 0)
      if (!best || currentLen < best.totalLength_mm || (currentLen === best.totalLength_mm && pieces <= best.pieces)) {
        best = { counts: { ...counts }, totalLength_mm: currentLen, pieces }
      }
      return
    }

    for (let i = stageIndex; i < sorted.length; i++) {
      const size = sorted[i]
      counts[size] = (counts[size] ?? 0) + 1
      recurse(currentLen + RAIL_LENGTHS_MM[size], counts, i)
      counts[size]! -= 1
      if (counts[size] === 0) delete counts[size]
    }
  }

  recurse(0, {}, 0)
  return best
}

/**
 * Parse the user's `preferredRails` dropdown value into an array of RailSize.
 * Values: 'all', '94', '139', '185', '94_139', '94_185', '139_185'.
 */
export function parsePreferredRails(pref: string): RailSize[] {
  if (!pref || pref === 'all') return [94, 139, 185]

  const sizes: RailSize[] = []
  if (pref.includes('94')) sizes.push(94)
  if (pref.includes('139')) sizes.push(139)
  if (pref.includes('185')) sizes.push(185)
  return sizes
}

/** Parse `preferredRails` from integration_json (string or checkbox array). */
export function parsePreferredRailsFromOptions(pref: unknown): RailSize[] {
  if (Array.isArray(pref)) {
    if (pref.length === 0) return []
    return parsePreferredRails(pref.join('_'))
  }
  return parsePreferredRails(typeof pref === 'string' ? pref : 'all')
}

// --- Anchor / Mount Quantity ---

/**
 * Anchor qty for asphalt shingle anchors (FR-TALON-XE and FR-RT-MINI-BOX-20).
 * FR BOM Builder / Charge Solar Ultrabuilder flowchart:
 *   anchors = max(4, floor(((rowLen_in − span_in/2) / span_in + 1.95) × 2))
 *
 * Both rails are doubled before flooring — do not use floor(per-rail) × 2.
 */
export function computeAsphaltAnchorQty(rowLen_in: number, span_in: number): number {
  if (span_in <= 0) return 4
  return Math.max(4, Math.floor(((rowLen_in - span_in / 2) / span_in + 1.95) * 2))
}

/**
 * Anchor qty for metal roof anchors (FR-S-5-CLAMPS, FR-PM-BRACKET, FR-EJOT, FR-S-5-RIBBRACKET).
 * Calculated span is snapped to pitch distance before computing qty.
 *   snappedSpan = floor(calculatedSpan_in / pitchDistance) × pitchDistance
 *   anchors = max(4, ceil(2 × floor(rowLen_in / snappedSpan) + 3))
 */
export function computeMetalAnchorQty(rowLen_in: number, calculatedSpan_in: number, pitchDistance_in: number): number {
  if (pitchDistance_in <= 0 || calculatedSpan_in <= 0) return 4
  const snappedSpan = Math.floor(calculatedSpan_in / pitchDistance_in) * pitchDistance_in
  if (snappedSpan <= 0) return 4
  return Math.max(4, Math.ceil(2 * Math.floor(rowLen_in / snappedSpan) + 3))
}

export interface AnchorResult {
  anchorQty: number
  footKitQty: number
}

/**
 * Compute anchor and foot kit quantities for a single contiguous row.
 * `span_in` is the effective span: calculated for Talon/metal, user-entered for RT-MINI.
 * `pitchDistance_in` is only used for metal anchors (seamSpacing or majorRibSpacing).
 */
export function computeAnchorQty(
  anchor: AnchorType,
  rowLen_mm: number,
  span_in: number,
  pitchDistance_in: number
): AnchorResult {
  const rowLen_in = rowLen_mm / 25.4
  const group = anchorGroup(anchor)

  let anchorQty: number
  if (group === 'asphaltShingle') {
    anchorQty = computeAsphaltAnchorQty(rowLen_in, span_in)
  } else {
    anchorQty = computeMetalAnchorQty(rowLen_in, span_in, pitchDistance_in)
  }

  return { anchorQty, footKitQty: anchorQty }
}

/**
 * NBC 2020 roof snow load for Fast Rack span lookup (Ultrabuilder formula).
 * S = Is × (Ss×Cb×Cw×Cs×Ca + Sr) × cos²(slope)
 */
export function computeFastRackDesignSnow_kPa(Ss_kPa: number, Sr_kPa: number, slopeDeg: number): number {
  const Is = 1
  const Cb = 0.8
  const Cw = 1
  const Ca = 1
  let Cs: number
  if (slopeDeg < 15) {
    Cs = 1
  } else if (slopeDeg <= 60) {
    Cs = (60 - slopeDeg) / 45
  } else {
    Cs = 0
  }

  const slopeRad = (slopeDeg * Math.PI) / 180
  const designSnow_kPa = Is * (Ss_kPa * Cb * Cw * Cs * Ca + Sr_kPa) * Math.cos(slopeRad) ** 2
  return Math.round(designSnow_kPa * 100) / 100
}

// --- Accessories ---

export interface BlockPerimeterParams {
  panelWidth_in: number
  panelHeight_in: number
  shortSides: number
  longSides: number
  useHiddenEndClamps: boolean
  thermalExpansionJoints: number
}

function panelEdgeGap_in(): number {
  return panelMmToInches(PANEL_EDGE_GAP_MM)
}

function thermalJointWidth_in(useHiddenEndClamps: boolean): number {
  return useHiddenEndClamps ? THERMAL_JOINT_RAIL_GAP_IN : THERMAL_JOINT_MODULE_GAP_IN
}

/** Sum splice breaks across all rows (post-thermal row lengths). */
export function computeBlockThermalExpansionJoints(
  rowPanelCounts: number[],
  moduleLength_mm: number,
  useHiddenEndClamps: boolean,
  interModuleGap_mm = INTER_MODULE_GAP_MM
): number {
  return rowPanelCounts.reduce((sum, numPanels) => {
    if (numPanels <= 0) return sum
    const rowLen = computeRowLength(numPanels, moduleLength_mm, useHiddenEndClamps, interModuleGap_mm)
    return sum + computeSpliceBreaks(rowLen)
  }, 0)
}

/** Long-side perimeter length (row ends + thermal joints) in inches. */
export function computeLongSideLength_in(params: BlockPerimeterParams): number {
  const gap_in = panelEdgeGap_in()
  let length_in = (params.panelHeight_in + gap_in) * params.longSides
  if (!params.useHiddenEndClamps) {
    length_in += panelMmToInches(END_BUFFER_MM)
  }
  length_in += params.thermalExpansionJoints * 2 * thermalJointWidth_in(params.useHiddenEndClamps)
  return length_in
}

/** Short-side perimeter length (module width edges only) in inches. */
export function computeShortSideLength_in(params: BlockPerimeterParams): number {
  const gap_in = panelEdgeGap_in()
  return (params.panelWidth_in + gap_in) * params.shortSides
}

/**
 * Block perimeter for edge trim / critter guard: base ½" gap per edge,
 * optional 4" end buffer (standard clamps), and thermal expansion at splice breaks.
 */
export function computeBlockPerimeter_in(params: BlockPerimeterParams): number {
  return computeLongSideLength_in(params) + computeShortSideLength_in(params)
}

/** @deprecated Use `computeBlockPerimeter_in` with full block params. Base gap only. */
export function computePerimeter_in(
  panelWidth_in: number,
  panelHeight_in: number,
  shortSides: number,
  longSides: number
): number {
  return computeBlockPerimeter_in({
    panelWidth_in,
    panelHeight_in,
    shortSides,
    longSides,
    useHiddenEndClamps: true,
    thermalExpansionJoints: 0,
  })
}

export function computeEndCapQty(contiguousRowCount: number, totalSpliceBreaks: number): number {
  return 4 * contiguousRowCount + 4 * totalSpliceBreaks
}

/**
 * Critter guard fence: roll count from perimeter.
 * Each roll is 100 ft. Perimeter is in inches.
 */
export function computeFenceRolls(perimeter_in: number): number {
  const perimeterFt = perimeter_in / 12
  return Math.ceil(perimeterFt / 100)
}

/**
 * J-hooks: one every 12" along long sides.
 * `longSideLength_in` = total length of long sides in inches.
 */
export function computeJhookQty(longSideLength_in: number): number {
  return Math.ceil(longSideLength_in / 12)
}

/**
 * Critter guard clips: 3 per module edge on short sides.
 * `shortSideModuleEdges` = number of module edges along short sides.
 */
export function computeCgClipQty(shortSideModuleEdges: number): number {
  return 3 * shortSideModuleEdges
}

/**
 * Edge clamps: one every ~18" along the given perimeter length (inches).
 */
export function computeEdgeClampQty(perimeter_in: number): number {
  return Math.ceil(perimeter_in / 18)
}

/**
 * Edge trim: pieces from perimeter, then boxes.
 * Each piece covers 42" of perimeter. Boxes of 25.
 */
export function computeEdgeTrimPieces(perimeter_in: number): number {
  return Math.ceil(perimeter_in / 42)
}
